import React, { Component } from 'react';
import Icon from '@lib/components/Icon';

export interface NormaImage {
    src: string;
    alt?: string;
}

export interface GalleryImage {
    desktop: NormaImage;
    tablet: NormaImage;
    mobile: NormaImage;
}

export type GalleryData = GalleryImage[];

export interface GalleryProps {
    data: GalleryData;
}

export interface GalleryState {
    currentImage: number;
}

export class GalleryComponent extends Component<GalleryProps, GalleryState> {
    constructor(props: GalleryProps) {
        super(props);
        this.state = {
            currentImage: 0,
        };
    }

    render(): JSX.Element | null {
        const { data } = this.props;
        const { currentImage } = this.state;

        const images = data.map(({ desktop, tablet, mobile }, index) => {
            const classes = ['gallery__image', index === currentImage ? 'is-active' : ''].join(' ');
            return (
                <picture key={index}>
                    <source media="all and (max-width: 767px)" srcSet={mobile.src} />
                    <source media="all and (min-width: 768px) and (max-width: 1239px)" srcSet={tablet.src} />
                    <source media="all and (min-width: 1240px)" srcSet={desktop.src} />
                    <img className={classes} src={desktop.src} alt={desktop.alt} />
                </picture>
            );
        });

        const thumbnails = data.map(({ mobile }, index) => {
            const classes = ['gallery__thumbnail', index === currentImage ? 'is-active' : ''].join(' ');
            return (
                <button key={index} type="button" className={classes} onClick={() => this.changeCurrent(index)}>
                    <img src={mobile.src} alt={mobile.alt} />
                </button>
            );
        });

        const orbs = Array.from({ length: Math.min(5, data.length) }).map((_, index) => {
            return (
                <button
                    key={index}
                    onClick={() => this.changeCurrent(index)}
                    className="slider-bullet"
                    data-bullet-number={index}
                    aria-label={index.toString()}
                />
            );
        });

        return (
            <div className="gallery">
                {images}
                {data.length > 1 && <div className="gallery__thumbnails">{thumbnails}</div>}
                {data.length > 1 && (
                    <div className="gallery__mobile-navigation" data-slider-active={currentImage}>
                        <button
                            type="button"
                            className="button button--icon has-icon-lightblue"
                            onClick={() => this.changeCurrentRelative(-1)}
                            disabled={currentImage === 0}
                        >
                            <Icon icon="arrow-left" className="is-size-2rem" />
                        </button>
                        <div className="display-inline-flex">{orbs}</div>
                        <button
                            type="button"
                            className="button button--icon has-icon-lightblue"
                            onClick={() => this.changeCurrentRelative(1)}
                            disabled={currentImage === data.length - 1}
                        >
                            <Icon icon="arrow-right" className="is-size-2rem" />
                        </button>
                    </div>
                )}
            </div>
        );
    }

    changeCurrent(index: number): void {
        this.setState({ currentImage: index });
    }

    changeCurrentRelative(modifier: number): void {
        this.setState((state) => {
            return {
                currentImage: clamp(0, state.currentImage + modifier, this.props.data.length - 1),
            };
        });
    }
}

function clamp(min: number, value: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
